import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  PublicClientApplication, 
  AccountInfo, 
  AuthenticationResult,
  InteractionRequiredAuthError,
  EventType,
  EventMessage,
  AuthError
} from '@azure/msal-browser';
import { msalConfig, loginRequest, silentRequest } from '../config/authConfig';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../store/slices/authSlice';
import { socketService } from '../services/socket';

interface AzureAuthContextType {
  account: AccountInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: () => Promise<void>;
  autoLogin: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const AzureAuthContext = createContext<AzureAuthContextType>({
  account: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  login: async () => {},
  autoLogin: async () => {},
  logout: async () => {},
  getAccessToken: async () => null,
});

export const useAzureAuth = () => useContext(AzureAuthContext);

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

export const AzureAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const dispatch = useDispatch();

  // Initialize MSAL
  useEffect(() => {
    if (isInitialized) return;
    
    const initializeMsal = async () => {
      try {
        setIsInitialized(true);
        await msalInstance.initialize();
        
        // Handle redirect response if returning from Azure AD
        console.log('Checking for redirect response, URL:', window.location.href);
        const response = await msalInstance.handleRedirectPromise();
        console.log('handleRedirectPromise result:', response);
        if (response) {
          console.log('Redirect response received:', response.account?.username);
          await handleAuthResponse(response);
          return;
        }

        // Check current state
        const activeAccount = msalInstance.getActiveAccount();
        console.log('Active account:', activeAccount?.username);
        
        // Set up event callbacks
        msalInstance.addEventCallback((event: EventMessage) => {
          if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
            const payload = event.payload as AuthenticationResult;
            console.log('Login success event:', payload.account?.username);
            setAccount(payload.account);
          }
        });

        // Try automatic login
        await attemptSilentLogin();
      } catch (error) {
        console.error('MSAL initialization error:', error);
        if (error instanceof AuthError && error.errorCode === 'interaction_in_progress') {
          // Just wait, MSAL is already doing something
          console.log('Auth interaction already in progress, waiting...');
        } else {
          setError('Failed to initialize authentication');
          setIsLoading(false);
        }
      }
    };

    initializeMsal();
  }, [isInitialized]);

  // Handle successful authentication
  const handleAuthResponse = async (response: AuthenticationResult) => {
    if (!response.account) {
      setError('No account information received');
      setIsLoading(false);
      return;
    }

    setAccount(response.account);
    
    // Validate with backend
    try {
      const validated = await validateWithBackend(response);
      if (validated) {
        setIsLoading(false);
      } else {
        setError('Account not authorized for this application');
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Backend validation error:', err);
      setError('Failed to validate account');
      setIsLoading(false);
    }
  };

  // Validate Azure account with backend
  const validateWithBackend = async (authResponse: AuthenticationResult): Promise<boolean> => {
    try {
      console.log('Validating with backend...', {
        username: authResponse.account?.username,
        name: authResponse.account?.name,
        tenantId: authResponse.account?.tenantId
      });

      // Always validate with backend
      const wsUrl = import.meta.env.VITE_WEBSOCKET_URL || 'wss://engineer.mach0.net';

      // Connect WebSocket with Azure token (with timeout)
      const connectPromise = socketService.connect({
        token: authResponse.idToken,
        authType: 'azure'
      });
      
      // Add timeout to prevent hanging forever
      const timeoutPromise = new Promise<boolean>((resolve) => {
        setTimeout(() => {
          console.warn('WebSocket connection timed out after 10s');
          resolve(false);
        }, 10000);
      });
      
      const connected = await Promise.race([connectPromise, timeoutPromise]);
      console.log('WebSocket connection result:', connected);

      if (connected) {
        // Dispatch Redux action for successful login
        dispatch(loginSuccess({
          user: {
            id: authResponse.account?.localAccountId || '',
            email: authResponse.account?.username || '',
            name: authResponse.account?.name || '',
            partnerId: authResponse.account?.tenantId || '',
            loginId: authResponse.account?.username || '',
            role: 'user',
          },
          accessToken: authResponse.idToken,
          refreshToken: authResponse.accessToken,
          sessionId: authResponse.account?.localAccountId || '',
        }));
        
        console.log('Backend validation successful');
        return true;
      }
      
      console.error('WebSocket connection failed or timed out');
      
      // Even if WebSocket fails, we have valid Azure auth, so allow access
      console.warn('Allowing access with Azure auth despite WebSocket failure');
      dispatch(loginSuccess({
        user: {
          id: authResponse.account?.localAccountId || '',
          email: authResponse.account?.username || '',
          name: authResponse.account?.name || '',
          partnerId: authResponse.account?.tenantId || '',
          loginId: authResponse.account?.username || '',
          role: 'user',
        },
        accessToken: authResponse.idToken,
        refreshToken: authResponse.accessToken,
        sessionId: authResponse.account?.localAccountId || '',
      }));
      
      // Try to connect WebSocket in background
      setTimeout(() => {
        console.log('Attempting WebSocket reconnection in background...');
        socketService.connect({
          token: authResponse.idToken,
          authType: 'azure'
        });
      }, 2000);
      
      return true;
    } catch (error) {
      console.error('Backend validation failed:', error);
      
      // For development, still allow login even if backend fails
      if (process.env.NODE_ENV === 'development') {
        console.warn('Development mode: allowing login despite backend failure');
        
        dispatch(loginSuccess({
          user: {
            id: authResponse.account?.localAccountId || '',
            email: authResponse.account?.username || '',
            name: authResponse.account?.name || '',
            partnerId: authResponse.account?.tenantId || '',
            loginId: authResponse.account?.username || '',
            role: 'user',
          },
          accessToken: authResponse.idToken,
          refreshToken: authResponse.accessToken,
          sessionId: authResponse.account?.localAccountId || '',
        }));
        
        return true;
      }
      
      return false;
    }
  };

  // Attempt silent login
  const attemptSilentLogin = async () => {
    try {
      // Check for cached accounts
      const accounts = msalInstance.getAllAccounts();
      
      if (accounts.length > 0) {
        // Use first account
        const account = accounts[0];
        msalInstance.setActiveAccount(account);
        
        // Try to get token silently with forceRefresh to ensure fresh token
        const response = await msalInstance.acquireTokenSilent({
          ...silentRequest,
          account,
          forceRefresh: true // Force refresh to get a fresh token
        });
        
        console.log('Silent login successful:', response.account?.username);
        await handleAuthResponse(response);
        return;
      }

      // Try SSO silent for fresh sessions
      console.log('No cached accounts, attempting SSO silent...');
      try {
        const response = await msalInstance.ssoSilent({
          ...silentRequest,
          loginHint: undefined, // Let MSAL figure it out
        });
        console.log('SSO silent successful:', response.account?.username);
        await handleAuthResponse(response);
        return;
      } catch (ssoError) {
        console.log('SSO silent failed, user needs manual login:', ssoError);
      }

      setIsLoading(false);
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.log('Interaction required, user needs to login');
        setIsLoading(false);
      } else {
        console.error('Silent login error:', error);
        setError('Authentication failed');
        setIsLoading(false);
      }
    }
  };

  // Login function (manual trigger)
  const login = useCallback(async () => {
    try {
      console.log('Login function called, starting redirect...');
      setIsLoading(true);
      setError(null);
      
      // Ensure MSAL is initialized
      if (!isInitialized) {
        console.log('MSAL not initialized yet, waiting...');
        await msalInstance.initialize();
      }
      
      // Use redirect for better experience
      console.log('Calling loginRedirect with config:', loginRequest);
      await msalInstance.loginRedirect(loginRequest);
      console.log('Login redirect initiated');
    } catch (error) {
      console.error('Login error:', error);
      if (error instanceof AuthError) {
        if (error.errorCode === 'interaction_in_progress') {
          console.log('Interaction in progress, please wait');
          // Don't set error for this case
        } else {
          setError(error.message);
        }
      } else {
        setError('Login failed');
      }
      setIsLoading(false);
    }
  }, [isInitialized]);

  // Auto login function (tries popup first)
  const autoLogin = useCallback(async () => {
    try {
      console.log('Auto-login attempting popup method...');
      setIsLoading(true);
      setError(null);
      
      // Ensure MSAL is initialized
      if (!isInitialized) {
        await msalInstance.initialize();
      }
      
      // Try popup method which sometimes works without user gesture
      try {
        const response = await msalInstance.loginPopup(loginRequest);
        console.log('Popup login successful:', response.account?.username);
        await handleAuthResponse(response);
        return;
      } catch (popupError) {
        console.log('Popup blocked or failed:', popupError);
        // Fall back to showing button
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Auto-login error:', error);
      setIsLoading(false);
    }
  }, [isInitialized]);

  // Logout function
  const logout = async () => {
    try {
      // Clear local state
      setAccount(null);
      
      // Disconnect WebSocket
      socketService.disconnect();
      
      // Logout from Azure AD
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout error:', error);
      setError('Logout failed');
    }
  };

  // Get access token for API calls
  const getAccessToken = async (): Promise<string | null> => {
    if (!account) return null;

    try {
      const response = await msalInstance.acquireTokenSilent({
        ...silentRequest,
        account,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Token expired, need to re-authenticate
        await login();
        return null;
      }
      console.error('Token acquisition error:', error);
      return null;
    }
  };

  const contextValue: AzureAuthContextType = {
    account,
    isAuthenticated: !!account,
    isLoading,
    error,
    login,
    autoLogin,
    logout,
    getAccessToken,
  };

  return (
    <AzureAuthContext.Provider value={contextValue}>
      {children}
    </AzureAuthContext.Provider>
  );
};

export default AzureAuthProvider;